import {
  getStyles,
  getMaterials,
  getProfiles,
  getWindowTypes,
  getWindowOpeningTypes,
  getSafetyTypes,
  getInnerColorGroups,
  getOuterColorGroups,
  getRanges,
  getInnovations,
} from '@/api/index'
import { STEPS_BY_TYPE } from './constants'

const STEPS = {
  files: {
    visibleInMenu: false,
    defaultState: () => ({}),
    validate ({ files }) {
      return files.some(file => file.uploaded)
    },
  },
  style: {
    label: 'design',
    defaultState: () => ({ styles: [] }),
    formDefaults: () => ({ id: null, name: null }),
    async init ({ state }) {
      state.styles = await getStyles()
    },
    validate ({ form }) {
      return !!form.style.id
    },
  },
  material: {
    label: 'material',
    defaultState: () => ({ materials: [] }),
    formDefaults: () => ({ id: null, name: null }),
    async init ({ state, form }) {
      const { style } = form

      if (style.id) {
        state.materials = await getMaterials(style.id)
      }
    },
    validate ({ form, state }) {
      const { materials = [] } = state

      return (
        !!form.material.id && materials.some(x => x.id === form.material.id)
      )
    },
  },
  profile: {
    label: 'profile',
    defaultState: () => ({ profiles: [] }),
    formDefaults: () => ({ id: null, name: null }),
    async init ({ state, form }) {
      const { style, material } = form
      if (style.id && material.id) {
        state.profiles = await getProfiles(style.id, material.id)
      }
    },
    validate ({ form, state }) {
      const { profiles = [] } = state
      return !!form.profile.id && profiles.some(x => x.id === form.profile.id)
    },
  },
  windowType: {
    label: 'windowType',
    defaultState: () => ({ windowTypes: [] }),
    formDefaults: () => ({ id: null, name: null, photo: null }),
    async init ({ state, form }) {
      const { profile } = form
      if (profile.id) {
        state.windowTypes = await getWindowTypes(profile.id)
      }
    },
    validate ({ form, state }) {
      const { windowTypes } = state
      return (
        !!form.windowType.id &&
        windowTypes.some(x => x.id === form.windowType.id)
      )
    },
  },
  windowOpeningType: {
    label: 'opening',
    childOf: 'windowType',
    defaultState: () => ({ openingTypes: [] }),
    formDefaults: () => ({ id: null, name: null, photo: null }),
    async init ({ state, form }) {
      const { windowType } = form
      if (windowType.id) {
        state.openingTypes = await getWindowOpeningTypes(windowType.id)
      }
    },
    validate ({ form, state }) {
      const { openingTypes = [] } = state
      return (
        !!form.windowOpeningType.id &&
        openingTypes.some(x => x.id === form.windowOpeningType.id)
      )
    },
  },
  safety: {
    label: 'safety',
    defaultState: () => ({ safetyTypes: [] }),
    formDefaults: () => ({ id: null, name: null }),
    async init ({ state, form }) {
      const { profile } = form
      if (profile.id) {
        state.safetyTypes = await getSafetyTypes(profile.id)
      }
    },
    validate ({ form, state }) {
      const { safetyTypes = [] } = state
      return !!form.safety.id && safetyTypes.some(x => x.id === form.safety.id)
    },
  },
  colorInner: {
    label: 'internal',
    group: 'colors',
    defaultState: () => ({ groups: [] }),
    formDefaults: () => ({ id: null, name: null, photo: null }),
    async init ({ state, form }) {
      const { profile } = form

      if (profile.id) {
        state.groups = await getInnerColorGroups(profile.id)
      }
    },
    validate ({ form, state }) {
      const { groups = [] } = state
      const colors = groups.reduce(
        (result, group) => [...result, ...group.colors],
        [],
      )

      return (
        !!form.colorInner.id && colors.some(x => x.id === form.colorInner.id)
      )
    },
  },
  colorOuter: {
    label: 'external',
    group: 'colors',
    defaultState: () => ({ groups: [] }),
    formDefaults: () => ({ id: null, name: null, photo: null }),
    async init ({ state, form }) {
      const { profile } = form
      if (profile.id) {
        state.groups = await getOuterColorGroups(profile.id)
      }
    },
    validate ({ form, state }) {
      const { groups = [] } = state
      const colors = groups.reduce(
        (result, group) => [...result, ...group.colors],
        [],
      )
      return (
        !!form.colorOuter.id && colors.some(x => x.id === form.colorOuter.id)
      )
    },
  },
  sizeAndQuantity: {
    label: 'sizeAndQuantity',
    defaultState: () => ({ ranges: [] }),
    async init ({ state, form }) {
      const { windowOpeningType, profile } = form

      if (profile.id && windowOpeningType.id) {
        state.ranges = await getRanges(profile.id, windowOpeningType.id)
      }
    },
    validate ({ state, form }) {
      const { ranges = [] } = state

      const currentRange = ranges.find(range => {
        return form.width >= range.w_min && form.width <= range.w_max
      })

      if (!currentRange) {
        return false
      }

      const widthValid =
        form.width >= currentRange.w_min && form.width <= currentRange.w_max
      const heightValid =
        form.height >= currentRange.h_min && form.height <= currentRange.h_max

      return widthValid && heightValid && form.count > 0
    },
  },
  /*innovations: {
    label: 'innovations',
    defaultState: () => ({ innovatons: [], valid$: true }),
    formDefaults: () => [],
    async init({ state, form }) {
      const { profile } = form;
      if (profile.id) {
        state.innovations = await getInnovations(profile.id);
      }
    },
    validate({ form }) {
      return (
        !!form.profile.id &&
        Array.isArray(form.innovations) &&
        form.innovations.length >= 0
      );
    },
  },*/
  configurationName: {
    label: 'configurationName',
    defaultState: () => ({}),
    validate ({ form }) {
      return !!form.name
    },
  },
}

function getSteps (configurationType) {
  if (!configurationType) {
    throw new Error('Configuration type is required.')
  }

  const stepsIds = STEPS_BY_TYPE[configurationType]
  const steps = new Map()

  stepsIds.forEach(stepId => {
    if (!(stepId in STEPS)) {
      throw new Error(`Could not find step with id "${stepId}".`)
    }

    steps.set(stepId, STEPS[stepId])
  })

  return steps
}

function getStepById (stepId) {
  if (!stepId) {
    throw new Error('Step id is required!')
  }

  if (!(stepId in STEPS)) {
    throw new Error(`Could not find step with id "${stepId}".`)
  }

  return STEPS[stepId]
}

export { getSteps, getStepById }
